import { Card, Flex, Form, Spin, Typography, Skeleton as AntdSkeleton } from "antd";
import { observer } from "mobx-react-lite";
import { useEffect, useState, useRef, useMemo, useCallback } from "react";

import { CustomButton, OtpInput, OtpTimeLeft, Skeleton } from "ui";
import { NOTIFICATION_TYPE_ENUM, OPERATION_TYPE } from "interfaces";
import { generateOperation, createBidOperation } from "services";
import { useStores } from "stores";
import { useNotificationContext } from "providers";
import { parseErrorMessage, canSendSms, clearSmsData, requestWithNotify } from "utils";
import { SMS_CODE_RETRY_DELAY_IN_SECONDS } from "utils/envConstants";
import { ConfirmOtpModalTitle } from "./ConfirmOtpModalTitle";
import { ConfirmOtpModalDescription } from "./ConfirmOtpModalDescription";
import { ConfirmOtpModalProps } from "./_type";

const SkeletonComponent = ({ skeletonComponent }: { skeletonComponent: React.ReactNode }): JSX.Element => {
  return (
    <Flex vertical gap="large">
      {skeletonComponent || (
        <Flex vertical gap="small">
          <Skeleton width="80%" height="24px" />
          <Flex gap="small" vertical style={{ marginTop: "10px" }}>
            <Skeleton width="100%" height="16px" />
            <Skeleton width="100%" height="16px" />
            <Skeleton width="100%" height="16px" />
            <Skeleton width="100%" height="16px" />
          </Flex>
        </Flex>
      )}
      <Card className="card-not-shadow border-tertiary" style={{ width: "502px", height: "220px" }}>
        <Flex vertical gap="large">
          <Skeleton width="100%" height="16px" />
          <Flex gap="large" vertical>
            <Flex justify="center" align="center" gap="small">
              <Skeleton width="79px" height="56px" />
              <Skeleton width="79px" height="56px" />
              <Skeleton width="79px" height="56px" />
              <Skeleton width="79px" height="56px" />
            </Flex>
          </Flex>
          <CustomButton loading={true} style={{ width: "100%" }} />
        </Flex>
      </Card>
    </Flex>
  );
};

export const ConfirmOtpModal = observer(
  ({
    title,
    titleClassName,
    description,
    descriptionClassName,
    operationPath,
    notificationMsg,
    requestBody,
    requestId,
    cancelBtnProps,
    onSuccess,
    skeletonComponent,
  }: ConfirmOtpModalProps) => {
    const { actionModals, bidCard } = useStores();
    const { openNotification } = useNotificationContext();
    const id = requestId ? requestId : bidCard.bid.id;
    const { title: cancelBtnTitle = "Отмена", position = "middle" } = cancelBtnProps || {};

    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [otpValue, setOtpValue] = useState("");
    const [timeLeft, setTimeLeft] = useState(0);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);
    const [shouldStartOtpCountdown, setShouldStartOtpCountdown] = useState(false);

    const startOtpCountdown = () => {
      if (intervalRef.current) clearInterval(intervalRef.current);

      setTimeLeft(SMS_CODE_RETRY_DELAY_IN_SECONDS);
      setShouldStartOtpCountdown(true);

      intervalRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          const next = prev - 1;
          if (next <= 0) {
            clearInterval(intervalRef.current!);
            intervalRef.current = null;
            setShouldStartOtpCountdown(false);
            return 0;
          }
          return next;
        });
      }, 1000);
    };

    const sendSms = async () => {
      setLoading(true);
      if (!canSendSms(operationPath)) {
        openNotification({
          description: `Код можно отправить раз в ${SMS_CODE_RETRY_DELAY_IN_SECONDS} секунд`,
          type: NOTIFICATION_TYPE_ENUM.ERROR,
        });
        startOtpCountdown();
        setLoading(false);
        return;
      }

      try {
        await generateOperation(operationPath);
        form.setFieldsValue({ otp: "" });
        setOtpValue("");
        startOtpCountdown();
      } catch (e: any) {
        if (e.type === "/otp-error") {
          openNotification({
            description: e.title || `Код можно отправить раз в ${SMS_CODE_RETRY_DELAY_IN_SECONDS} секунд`,
            type: NOTIFICATION_TYPE_ENUM.ERROR,
          });
          startOtpCountdown();
        } else {
          openNotification({ description: parseErrorMessage(e), type: NOTIFICATION_TYPE_ENUM.ERROR });
        }
      } finally {
        setLoading(false);
      }
    };

    const handleCancel = () => {
      actionModals.closeAll();
    };

    const sendBid = async () => {
      setLoading(true);
      const values = form.getFieldsValue();
      const formData = JSON.stringify({
        otp: values.otp,
        ...requestBody,
      });

      try {
        await createBidOperation({ id: id, operationType: operationPath }, formData);

        if (onSuccess) {
          onSuccess?.();
        }

        form.resetFields();
        clearSmsData(operationPath);
        openNotification({
          message: !notificationMsg?.successMsg ? "Запрос успешно обработан" : notificationMsg?.successMsg,
          type: NOTIFICATION_TYPE_ENUM.SUCCESS,
        });
        handleCancel();
      } catch (e: any) {
        openNotification({
          description: !notificationMsg?.errorMsg ? parseErrorMessage(e) : notificationMsg.errorMsg,
          type: NOTIFICATION_TYPE_ENUM.ERROR,
        });
      } finally {
        setLoading(false);
      }
    };

    useEffect(() => {
      sendSms();
      // eslint-disable-next-line react-hooks/exhaustive-deps
      return () => {
        if (intervalRef.current) clearInterval(intervalRef.current);
        setTimeLeft(0);
        setShouldStartOtpCountdown(false);
      };
    }, []);

    return loading ? (
      <SkeletonComponent skeletonComponent={skeletonComponent} />
    ) : (
      <Flex vertical>
        <ConfirmOtpModalTitle title={title} titleClassName={titleClassName} />
        <ConfirmOtpModalDescription description={description} descriptionClassName={descriptionClassName} />

        <Form name="sendBid" form={form} initialValues={{ otp: "" }} onFinish={() => sendBid()} className="full-width">
          <Card className="card-not-shadow border-tertiary">
            <Flex vertical gap="20px">
              <Typography.Title level={4} className="margin-none text-center">
                Подтвердите действие кодом из смс:
              </Typography.Title>
              <OtpInput
                disabled={loading}
                onSendSms={sendSms}
                loading={loading}
                value={otpValue}
                onChange={setOtpValue}
              />

              <Form.Item>
                <Flex gap="large" justify="center">
                  {cancelBtnProps?.position === "middle" && (
                    <CustomButton
                      htmlType="button"
                      disabled={loading}
                      loading={loading}
                      type="default"
                      onClick={cancelBtnProps?.closeHandler ? cancelBtnProps?.closeHandler : handleCancel}
                      className="full-width"
                    >
                      {cancelBtnTitle}
                    </CustomButton>
                  )}
                  <CustomButton htmlType="submit" disabled={loading} loading={loading} className="full-width">
                    Подтвердить
                  </CustomButton>
                </Flex>
              </Form.Item>

              <OtpTimeLeft onResend={sendSms} loading={loading} shouldStartCountdown={shouldStartOtpCountdown} />
            </Flex>
          </Card>
        </Form>
        {cancelBtnProps?.position === "bottom" && (
          <CustomButton
            htmlType="button"
            disabled={loading}
            loading={loading}
            type="default"
            onClick={cancelBtnProps?.closeHandler ? cancelBtnProps?.closeHandler : handleCancel}
            className="full-width margin-top-sm"
          >
            {cancelBtnTitle}
          </CustomButton>
        )}
      </Flex>
    );
  }
);
